import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import type { Clock } from "@libs/checkout-core";
import {
  closeStopMoment,
  createPurchaseJourney,
  createPurchasePresenter,
  createPurchaseTiming,
  stopOnClose,
  type CloseStop,
  type CloseStopMoment,
  type PurchaseTransport,
  type PurchaseWarming,
} from "@libs/buy-on-behalf-client/purchase";
import type { AttemptMetrics, CheckoutSurface } from "@libs/buy-on-behalf-client/timing";

/**
 * A purchase checkout's journey and what to draw of it, for one access and
 * one signed-in buyer (LIS-1173). No DOM: the web and native renderers both
 * use it. A new transport (another account) or access is another journey;
 * the old one is disposed with its stream, so nothing it hears reaches the
 * new one.
 */
export function usePurchase({
  transport,
  accessId,
  restoreId,
  deliverTo,
  onAttempt,
  newId,
  clock,
  onHolding,
  onStoppable,
  onSucceeded,
  metrics,
  surface,
  warming,
}: {
  transport: PurchaseTransport;
  accessId: string;
  /** The delivery country the buyer's address named in an earlier checkout
   *  of this sheet: the purchase is read for it first. Read when a journey
   *  loads, as `restoreId` is. */
  deliverTo?: string | null;
  /** An attempt started before a reload, to pick up again: read when a
   *  journey loads, never again for a later change. */
  restoreId?: string | null;
  /** Where to keep the attempt for a reload (`null`: forget it). */
  onAttempt?: (id: string | null) => void;
  newId: () => string;
  clock: Clock;
  /** Something is moving the buyer may not walk away from: told the moment
   *  the journey says so, and `false` once this journey is gone. */
  onHolding?: (holding: boolean) => void;
  /** How the sheet's close stops the purchase, told at each change of its
   *  moment (`closeStopMoment`): asks while its view offers its stop (after
   *  Buy, before Pay), too late once Pay reached it, and null otherwise, or
   *  once this journey is gone. */
  onStoppable?: (stop: CloseStop | null) => void;
  /** The confirmed purchase is shown, after the status catches up. */
  onSucceeded?: () => void;
  /** The host's Sentry `metrics`: each press timed until its outcome shows. */
  metrics?: AttemptMetrics | null;
  surface?: CheckoutSurface;
  /** The purchase the sheet opened ahead of Buy, where it opened one: Buy
   *  authorises it. */
  warming?: PurchaseWarming | null;
}) {
  const remember = useRef(onAttempt);
  remember.current = onAttempt;
  const mint = useRef(newId);
  mint.current = newId;
  const time = useRef(clock);
  time.current = clock;
  const resume = useRef(restoreId);
  resume.current = restoreId;
  const readFor = useRef(deliverTo);
  readFor.current = deliverTo;
  const hold = useRef(onHolding);
  hold.current = onHolding;
  const stoppable = useRef(onStoppable);
  stoppable.current = onStoppable;
  const sink = useRef(metrics);
  sink.current = metrics;
  const where = useRef(surface);
  where.current = surface;
  const warm = useRef(warming);
  warm.current = warming;
  const { journey, timing } = useMemo(() => {
    let listing: string | null = null;
    const timing = createPurchaseTiming({
      surface: where.current,
      now: () => time.current.now(),
      listingId: () => listing,
      metrics: { distribution: (name, value, options) => sink.current?.distribution(name, value, options) },
    });
    const journey = createPurchaseJourney(transport, {
      newId: () => mint.current(),
      onAttempt: (id) => remember.current?.(id),
      timing,
      warming: () => warm.current,
    });
    journey.subscribe(() => {
      listing = journey.snapshot().purchase?.listingId ?? listing;
    });
    return { timing, journey };
  }, [transport]);
  const presenter = useMemo(
    () => createPurchasePresenter(journey, time.current, { onShown: (attempt) => timing.shown(attempt) }),
    [journey, timing],
  );
  useEffect(() => {
    let holding = false;
    const unsubscribe = presenter.subscribe(() => {
      const next = presenter.getSnapshot().holding;
      if (next === holding) return;
      holding = next;
      hold.current?.(next);
    });
    let moment: CloseStopMoment = null;
    const stop = () => stopOnClose(journey);
    const unwatchStop = journey.subscribe(() => {
      const next = closeStopMoment(journey.snapshot());
      if (next === moment) return;
      moment = next;
      stoppable.current?.(next ? { moment: next, stop } : null);
    });
    presenter.start();
    journey.load(accessId, resume.current, { deliverTo: readFor.current }).catch(() => undefined);
    // Only a signed-in buyer's checkout is up: their email is confirmed.
    warm.current?.emailConfirmed().catch(() => undefined);
    return () => {
      unsubscribe();
      unwatchStop();
      presenter.stop();
      journey.dispose();
      hold.current?.(false);
      if (moment) stoppable.current?.(null);
    };
  }, [journey, presenter, accessId]);
  const snapshot = useSyncExternalStore(presenter.subscribe, presenter.getSnapshot, presenter.getSnapshot);
  const success = snapshot.line?.mark === "done";
  useEffect(() => {
    if (success) onSucceeded?.();
  }, [success, onSucceeded]);
  return { journey, snapshot };
}
